import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { MailCheck, ArrowRight, RotateCw, Loader2, Inbox, ShieldCheck } from "lucide-react";

const dashboardFor = (user) =>
    user && (user.role === "seller" || user.role === "both") ? "/dashboard/seller" : "/dashboard/buyer";

export default function EmailSent() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const email = location.state?.email || user?.email || "";
    const [resending, setResending] = useState(false);

    // Guard: a user whose email is already verified has nothing to do here.
    useEffect(() => {
        if (user?.email_verified) navigate(dashboardFor(user), { replace: true });
    }, [user, navigate]);
    if (user?.email_verified) return null;

    const resend = async () => {
        setResending(true);
        try {
            const { data } = await api.post("/auth/resend-email");
            if (data.dev_email_token) {
                toast.message("Dev email code", { description: `Your verification code: ${data.dev_email_token}` });
            } else {
                toast.success("Verification email sent", {
                    description: email ? `We re-sent your code to ${email}.` : "Check your inbox for the new code.",
                });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not resend the email. Please try again.");
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-[80vh] max-w-2xl items-center px-4 py-12 sm:px-6">
            <Card
                data-testid="email-sent-card"
                className="noise-overlay relative w-full overflow-hidden border-border bg-card p-8 text-center shadow-[0_20px_60px_rgba(0,0,0,0.35)] sm:p-12"
            >
                {/* Soft radial accent — kept under 20% of the surface */}
                <div className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-40 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(240,180,41,0.16)_0%,rgba(240,180,41,0)_70%)]" />

                <div className="relative z-10 flex flex-col items-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brass/15 text-brass ring-1 ring-brass/30">
                        <MailCheck className="h-8 w-8" aria-hidden />
                    </span>

                    <h1 data-testid="email-sent-title" className="mt-6 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
                        Check your email
                    </h1>

                    <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
                        We&rsquo;ve sent a verification code to{" "}
                        {email ? (
                            <span data-testid="email-sent-address" className="font-semibold text-foreground">{email}</span>
                        ) : (
                            <span className="font-semibold text-foreground">your inbox</span>
                        )}
                        . Enter it on the next screen to verify your account and start placing sealed bids.
                    </p>

                    <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
                        <Button
                            data-testid="email-sent-enter-code-btn"
                            size="lg"
                            className="w-full rounded-[10px] font-semibold"
                            onClick={() => navigate("/account/verify")}
                        >
                            Enter verification code <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Button>
                        <Button
                            data-testid="email-sent-resend-btn"
                            size="lg"
                            variant="outline"
                            className="w-full rounded-[10px] border-electric/40 text-foreground hover:bg-electric/10"
                            onClick={resend}
                            disabled={resending}
                        >
                            {resending ? (
                                <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Resending&hellip;</>
                            ) : (
                                <><RotateCw className="mr-1.5 h-4 w-4" /> Resend email</>
                            )}
                        </Button>
                    </div>

                    {/* Help row */}
                    <div className="mt-8 w-full max-w-sm space-y-3 rounded-xl border border-border bg-secondary/30 p-4 text-left">
                        <p className="flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
                            <Inbox className="mt-0.5 h-4 w-4 shrink-0 text-electric" />
                            <span>Can&rsquo;t find it? Check your <span className="font-medium text-foreground">spam</span> or <span className="font-medium text-foreground">promotions</span> folder &mdash; it can take a minute to arrive.</span>
                        </p>
                        <p className="flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
                            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                            <span>Codes expire for your security &mdash; request a fresh one any time with <span className="font-medium text-foreground">Resend email</span>.</span>
                        </p>
                    </div>

                    <p className="mt-6 text-xs text-muted-foreground">
                        Wrong address?{" "}
                        <Link to="/signup" data-testid="email-sent-restart-link" className="font-medium text-electric hover:underline">
                            Sign up again
                        </Link>
                    </p>
                </div>
            </Card>
        </div>
    );
}
